import { useCallback, useRef, useEffect } from 'react'
import { useAvatarStore } from '@/stores'
import {
  tavusProvider,
  anamProvider,
  type IAvatarProvider,
  type AvatarSession,
  type AvatarSessionOptions,
  type AvatarSessionEvents,
  type AvatarProviderType,
  type ExpressionType,
} from '@/connectors/avatar'

interface UseAvatarOptions {
  onVideoReady?: (videoElement: HTMLVideoElement) => void
  onMessage?: (message: string) => void
  onTranscript?: (text: string, isFinal: boolean) => void
  onError?: (error: Error) => void
}

/** Get the provider instance for a given type. */
function getProvider(type: AvatarProviderType): IAvatarProvider {
  switch (type) {
    case 'anam':
      return anamProvider
    case 'tavus':
    default:
      return tavusProvider
  }
}

export function useAvatar(options: UseAvatarOptions = {}) {
  const {
    isInitialized,
    provider,
    avatars,
    sessionState,
    isConnected,
    isSpeaking,
    currentExpression,
    videoElement,
    isVideoReady,
    currentTranscript,
    agentMessage,
    videoQuality,
    enableEmotions,
    setInitialized,
    setAvatars,
    setSessionState,
    setVideoElement,
    setVideoReady,
    setCurrentTranscript,
    setAgentMessage,
    setExpression,
    reset,
  } = useAvatarStore()

  const sessionRef = useRef<AvatarSession | null>(null)
  const activeProviderRef = useRef<IAvatarProvider | null>(null)

  // Initialize the avatar provider
  const initialize = useCallback(async (apiKey: string, providerType: AvatarProviderType = 'tavus') => {
    try {
      const providerInstance = getProvider(providerType)
      await providerInstance.initialize(apiKey)
      const fetchedAvatars = await providerInstance.getAvatars()
      activeProviderRef.current = providerInstance
      setAvatars(fetchedAvatars)
      setInitialized(true, providerType)
    } catch (error) {
      console.error('Failed to initialize avatar provider:', error)
      options.onError?.(error instanceof Error ? error : new Error('Failed to initialize'))
      throw error
    }
  }, [setInitialized, setAvatars, options])

  // Start an avatar session
  const startSession = useCallback(async (sessionOptions: Omit<AvatarSessionOptions, 'videoQuality' | 'enableEmotions'>) => {
    if (!activeProviderRef.current) {
      throw new Error('Avatar provider not initialized')
    }

    if (sessionRef.current) {
      await sessionRef.current.stop()
    }

    const fullOptions: AvatarSessionOptions = {
      ...sessionOptions,
      videoQuality,
      enableEmotions,
    }

    const events: AvatarSessionEvents = {
      onStateChange: (state) => {
        setSessionState(state)
      },
      onVideoReady: (video) => {
        setVideoElement(video)
        setVideoReady(true)
        options.onVideoReady?.(video)
      },
      onMessage: (message) => {
        setAgentMessage(message)
        options.onMessage?.(message)
      },
      onTranscript: (text, isFinal) => {
        setCurrentTranscript(text)
        options.onTranscript?.(text, isFinal)
      },
      onError: (error) => {
        options.onError?.(error)
      },
    }

    try {
      const session = await activeProviderRef.current.createSession(fullOptions, events)
      sessionRef.current = session
      await session.start()
    } catch (error) {
      console.error('Failed to start avatar session:', error)
      options.onError?.(error instanceof Error ? error : new Error('Failed to start session'))
      throw error
    }
  }, [
    videoQuality,
    enableEmotions,
    setSessionState,
    setVideoElement,
    setVideoReady,
    setAgentMessage,
    setCurrentTranscript,
    options,
  ])

  // Stop the avatar session
  const stopSession = useCallback(async () => {
    if (sessionRef.current) {
      await sessionRef.current.stop()
      sessionRef.current = null
    }

    setVideoElement(null)
    setVideoReady(false)
  }, [setVideoElement, setVideoReady])

  // Send text message for avatar to speak
  const sendMessage = useCallback((text: string) => {
    if (!sessionRef.current) {
      console.warn('No active avatar session')
      return
    }

    sessionRef.current.sendText(text)
  }, [])

  // Set avatar expression
  const setAvatarExpression = useCallback((expression: ExpressionType) => {
    if (!sessionRef.current) {
      console.warn('No active avatar session')
      return
    }

    sessionRef.current.setExpression(expression)
    setExpression(expression)
  }, [setExpression])

  // Interrupt the avatar
  const interrupt = useCallback(() => {
    if (!sessionRef.current) {
      return
    }

    sessionRef.current.interrupt()
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopSession()
    }
  }, [stopSession])

  // Get the current session (for AvatarRAGEngine integration)
  const getSession = useCallback(() => sessionRef.current, [])

  return {
    // State
    isInitialized,
    provider,
    avatars,
    sessionState,
    isConnected,
    isSpeaking,
    currentExpression,
    videoElement,
    isVideoReady,
    currentTranscript,
    agentMessage,

    // Actions
    initialize,
    startSession,
    stopSession,
    sendMessage,
    setExpression: setAvatarExpression,
    interrupt,
    getSession,
    reset,
  }
}
